import React, { useState, useEffect, useRef } from 'react';
import {
  Buildings, FileText, CheckSquare, Square, Info, ArrowClockwise, Check, X, ArrowRight,
} from '@phosphor-icons/react';
import {
  getBranchNames, getSjvNo, updateSjvStatus, saveData,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import SectionLock from './SectionLock';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

function SjvPermissionForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchList, setBranchList] = useState([]);
  const [sjvList, setSjvList] = useState([]);

  const [cao, setCao] = useState('');
  const [sjvNo, setSjvNo] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [reason, setReason] = useState('');

  // tracked to mirror Angular's oldSjvStatus (not auto-applied to the checkbox)
  const oldSjvStatusRef = useRef(null);

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingBranchCode, setPendingBranchCode] = useState(null);
  const [pendingSjvNo, setPendingSjvNo] = useState(null);
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    getBranchNames().then((res) => setBranchList(res || [])).catch(() => setBranchList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const preventExceedLength = (event, max) => {
    const input = event.target;
    if (
      input.value.length >= max &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      event.key !== 'Tab'
    ) {
      event.preventDefault();
    }
  };

  const handleBranchChange = async (branchCode) => {
    setCao(branchCode);
    setSjvNo('');
    setIsChecked(false);
    oldSjvStatusRef.current = null;
    setSjvList([]);

    if (!branchCode) return;

    try {
      const res = await getSjvNo(branchCode);
      setSjvList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setSjvList([]);
    }
  };

  const handleSjvChange = (transNo) => {
    setSjvNo(transNo);
    setIsChecked(false); // reset checkbox on SJV change — user must tick manually

    if (transNo) {
      const match = sjvList.find((x) => String(x.transacion_no) === String(transNo));
      oldSjvStatusRef.current = match ? true : null;
    } else {
      oldSjvStatusRef.current = null;
    }
  };

  const reasonTrimLen = reason.trim().length;
  const caoInvalid = touched && !cao;
  const sjvInvalid = touched && !sjvNo;
  const reasonInvalid = touched && (reasonTrimLen < 10 || reason.length > 200);

  const isFormValid = !!cao && !!sjvNo && reasonTrimLen >= 10 && reason.length <= 200;

  const handleSave = () => {
    setTouched(true);

    if (!isFormValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    if (!isChecked) {
      showToast('warn', 'Permission Not Confirmed', 'Please tick the checkbox to confirm the transaction type change.');
      return;
    }

    setPendingBranchCode(cao);
    setPendingSjvNo(sjvNo);
    setPendingReason(reason.trim());
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  const doSave = async () => {
    const branchCode = pendingBranchCode;
    const sjv = pendingSjvNo;
    const trimmedReason = pendingReason;

    const audit = {
      schemaName: branchCode,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'SJV_CANCEL_PERMISSION',
      oldData: { branchCode, sjvNo: sjv, transactionType: 'A' },
      newData: { branchCode, sjvNo: sjv, transactionType: 'M' },
      reason: trimmedReason,
    };

    setSaving(true);
    try {
      await updateSjvStatus(branchCode, sjv);
      try {
        await saveData([audit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Saved successfully and audit saved.', true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Saved, but audit log failed to save.', true);
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Update failed:', err);
      showToast('error', 'Update Failed', 'Could not save the permission change. Please try again.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    setSjvNo('');
    setIsChecked(false);
    setReason('');
    setSjvList([]);
    oldSjvStatusRef.current = null;
    setTouched(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="sjv-permission-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="sjv-permission-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Transaction Configuration</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>SJV Cancel Permission</div>
            </div>
          </div>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600',
              color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', padding: '4px 10px', borderRadius: '999px',
            }}
          >
            <Info size={12} /> In Progress
          </span>
        </div>

        {/* SECTION A: SJV Selection */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select SJV</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch and SJV number to permit cancellation</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Buildings size={13} /> CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchList}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_code}
              value={cao}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="sjv-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <FileText size={13} /> SJV No. <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`sjv-${formKey}`}
              options={sjvList}
              getLabel={(o) => o.transacion_no}
              getValue={(o) => o.transacion_no}
              value={sjvNo}
              onChange={handleSjvChange}
              placeholder="Select SJV No"
              emptyMessage="Select a branch first"
              disabled={!cao}
              error={sjvInvalid}
              testId="sjv-no-select"
            />
            {sjvInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>SJV No is required</div>}
            {cao && sjvList.length === 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
                <Info size={12} /> No SJV numbers found for this branch
              </div>
            )}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!sjvNo} hint="Select a branch and SJV number in section A first">
        {/* SECTION B: Permission */}
        <div
          onClick={() => setIsChecked((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px',
            border: '1px solid #E5E7EB', borderRadius: '8px', background: isChecked ? '#EFF4FF' : '#F9FAFB',
            cursor: 'pointer', userSelect: 'none',
          }}
          data-testid="sjv-permission-checkbox"
        >
          {isChecked
            ? <CheckSquare size={20} color="#002FA7" weight="fill" />
            : <Square size={20} color="#9CA3AF" />}
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
            Permission to Change Transaction Type
          </span>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!sjvNo} hint="Select a branch and SJV number in section A first">
        {/* SECTION C: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reasonTrimLen >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={3}
          maxLength={200}
          placeholder="Enter reason (10–200 characters)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => preventExceedLength(e, 200)}
          data-testid="sjv-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reasonTrimLen === 0 ? 'Reason is required' : reasonTrimLen < 10 ? 'Minimum 10 characters required' : 'Maximum 200 characters allowed'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="sjv-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving} data-testid="sjv-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="sjv-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FileText size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm SJV Cancel Permission</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You're about to change the transaction type for this SJV. Please review before confirming.
              </p>

              <div style={{ display: 'flex', gap: '24px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Branch</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingBranchCode}</p>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>SJV No</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingSjvNo}</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Transaction Type (Old)</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>A</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Transaction Type (New)</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>M</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="sjv-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={doSave} disabled={saving} className="btn-primary" data-testid="sjv-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SjvPermissionForm;